import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Download, Search, Mail, Phone, Building2, Package,
  MapPin, MessageSquare, Trash2, RefreshCw, Filter, Inbox, CheckCircle2, Clock, TrendingUp, KeyRound
} from "lucide-react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const statusStyles = {
  new: "bg-brand-gold/15 text-brand-gold border-brand-gold/40",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  closed: "bg-neutral-100 text-neutral-600 border-neutral-300",
};

function StatCard({ icon: Icon, label, value, tone = "green", testid }) {
  const bg = tone === "gold" ? "bg-brand-gold/10 text-brand-gold" : "bg-brand-green/10 text-brand-green";
  return (
    <div data-testid={testid} className="bg-white rounded-lg border border-neutral-200 p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-md flex items-center justify-center ${bg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-brand-charcoal/60 font-semibold">{label}</div>
        <div className="font-display font-bold text-2xl text-brand-charcoal">{value}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [i, s] = await Promise.all([api.get("/admin/inquiries"), api.get("/admin/stats")]);
      setInquiries(i.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return inquiries.filter((i) => {
      if (statusFilter !== "all" && (i.status || "new") !== statusFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        (i.name || "").toLowerCase().includes(s) ||
        (i.company || "").toLowerCase().includes(s) ||
        (i.email || "").toLowerCase().includes(s) ||
        (i.product_interest || "").toLowerCase().includes(s) ||
        (i.destination_country || "").toLowerCase().includes(s) ||
        (i.message || "").toLowerCase().includes(s)
      );
    });
  }, [inquiries, q, statusFilter]);

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Status updated");
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const removeInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry deleted");
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch {
      toast.error("Failed to delete inquiry");
    }
  };

  const exportCsv = () => {
    const token = getToken();
    const url = `${API_BASE}/admin/inquiries/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `aadlabs-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-cream">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-2xl text-brand-charcoal">Admin Dashboard</h1>
            <p className="text-xs text-brand-charcoal/60 mt-0.5">
              Signed in as <span className="text-brand-green font-semibold">{user?.email}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="btn-outline-green" style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}>
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button data-testid="admin-export-csv" onClick={exportCsv} className="btn-gold" style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}>
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <Link data-testid="admin-change-password" to="/admin/change-password" className="btn-outline-green" style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}>
              <KeyRound className="w-4 h-4" />
              Change Password
            </Link>
            <button data-testid="admin-logout" onClick={doLogout} className="btn-outline-green" style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}>
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <StatCard icon={Inbox} label="Total Inquiries" value={stats.total} tone="green" testid="stat-total" />
            <StatCard icon={Clock} label="New" value={stats.new} tone="gold" testid="stat-new" />
            <StatCard icon={CheckCircle2} label="Contacted" value={stats.contacted} tone="green" testid="stat-contacted" />
            <StatCard icon={CheckCircle2} label="Closed" value={stats.closed} tone="green" testid="stat-closed" />
            <StatCard icon={TrendingUp} label="Last 7 Days" value={stats.last_7_days} tone="gold" testid="stat-week" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-brand-cream border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
              placeholder="Search by name, company, email, product, country…"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-neutral-400" />
            <select
              data-testid="admin-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 bg-brand-cream border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold"
            >
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="text-sm text-brand-charcoal/60" data-testid="admin-count">
            {filtered.length} of {inquiries.length}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-brand-charcoal/60">Loading inquiries…</div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="p-16 text-center">
              <Inbox className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
              <div className="font-semibold text-brand-charcoal">No inquiries yet</div>
              <div className="text-sm text-brand-charcoal/60 mt-1">New B2B inquiries will appear here.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-cream border-b border-neutral-200 text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-brand-charcoal/70 uppercase text-xs tracking-wider">Received</th>
                    <th className="px-4 py-3 font-semibold text-brand-charcoal/70 uppercase text-xs tracking-wider">Contact</th>
                    <th className="px-4 py-3 font-semibold text-brand-charcoal/70 uppercase text-xs tracking-wider">Product / Qty</th>
                    <th className="px-4 py-3 font-semibold text-brand-charcoal/70 uppercase text-xs tracking-wider">Country</th>
                    <th className="px-4 py-3 font-semibold text-brand-charcoal/70 uppercase text-xs tracking-wider">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((i) => (
                    <tr
                      key={i.id}
                      data-testid={`inquiry-row-${i.id}`}
                      onClick={() => setSelected(i)}
                      className="border-b border-neutral-100 hover:bg-brand-cream/60 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 text-xs text-brand-charcoal/70 whitespace-nowrap">{fmtDate(i.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-brand-charcoal">{i.name}</div>
                        {i.company && <div className="text-xs text-brand-charcoal/60">{i.company}</div>}
                        <div className="text-xs text-brand-green mt-0.5">{i.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-brand-charcoal">{i.product_interest || "—"}</div>
                        <div className="text-xs text-brand-charcoal/60">{i.quantity || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-brand-charcoal">{i.destination_country || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider border ${statusStyles[i.status || "new"]}`}>
                          {i.status || "new"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-brand-green text-xs font-semibold">View →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="inquiry-detail-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setSelected(null)} />
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-neutral-200 flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-gold font-bold">Inquiry Details</div>
                <h2 className="mt-1 font-display font-bold text-xl text-brand-charcoal">{selected.name}</h2>
                {selected.company && <div className="text-sm text-brand-charcoal/60">{selected.company}</div>}
              </div>
              <button data-testid="close-drawer" onClick={() => setSelected(null)} className="text-brand-charcoal/60 hover:text-brand-charcoal">
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <Field icon={Mail} label="Email">
                <a href={`mailto:${selected.email}`} className="text-brand-green hover:underline">{selected.email}</a>
              </Field>
              {selected.phone && (
                <Field icon={Phone} label="Phone">
                  <a href={`tel:${selected.phone}`} className="text-brand-green hover:underline">{selected.phone}</a>
                </Field>
              )}
              {selected.company && <Field icon={Building2} label="Company">{selected.company}</Field>}
              {selected.product_interest && <Field icon={Package} label="Product Interest">{selected.product_interest}</Field>}
              {selected.quantity && <Field icon={Package} label="Quantity">{selected.quantity}</Field>}
              {selected.destination_country && <Field icon={MapPin} label="Destination">{selected.destination_country}</Field>}
              <Field icon={MessageSquare} label="Message">
                <div className="whitespace-pre-wrap text-brand-charcoal">{selected.message}</div>
              </Field>

              <div className="pt-4 border-t border-neutral-200">
                <div className="text-xs uppercase tracking-widest text-brand-gold font-bold mb-3">Update Status</div>
                <div className="flex flex-wrap gap-2">
                  {["new", "contacted", "closed"].map((s) => (
                    <button
                      key={s}
                      data-testid={`set-status-${s}`}
                      onClick={() => updateStatus(selected.id, s)}
                      className={`px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider border transition-colors ${
                        (selected.status || "new") === s ? statusStyles[s] : "bg-white text-brand-charcoal/60 border-neutral-200 hover:border-brand-green"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-neutral-200 flex flex-wrap gap-3">
                <a href={`mailto:${selected.email}?subject=Re: Your Aad Labs inquiry`} className="btn-green" style={{ padding: "0.6rem 1rem", fontSize: "0.85rem" }}>
                  <Mail className="w-4 h-4" /> Reply by Email
                </a>
                <button
                  data-testid="delete-inquiry"
                  onClick={() => removeInquiry(selected.id)}
                  className="btn-outline-green"
                  style={{ padding: "0.6rem 1rem", fontSize: "0.85rem", borderColor: "#dc2626", color: "#dc2626" }}
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>

              <div className="pt-4 text-xs text-brand-charcoal/50">
                Received on {fmtDate(selected.created_at)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3">
      <Icon className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/50 font-semibold">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}
